import { useMemo } from 'react';
import { searchCatalogueTools } from '../features/tools/toolRegistry';

/**
 * Natural-language intent parser for the assistant bar.
 * No LLM — pure pattern matching on French teacher commands.
 *
 * Returns an array of proposals, each:
 *   { id, intent, label, desc, icon, confidence, payload }
 *
 * Intents:
 *   record_payment   — "victor m'a payé 30 euros"
 *   generate_invoice  — "générer facture 2025 pour dupont"
 *   start_course      — "cours avec emma" / "commencer un cours"
 *   view_student      — "fiche de lucas" / "voir lucas"
 *   view_family       — "famille martin"
 *   send_invoice      — "envoyer facture à dupont"
 *   add_student       — "inscrire un élève"
 *   add_family        — "inscrire une famille"
 *   schedule          — "planifier cours"
 *   stats             — "CA mensuel" / "statistiques"
 *   settings          — "paramètres" / "profil"
 *   urssaf            — "avance immédiate" / "urssaf"
 *   relance_all       — "relancer les impayés" / "relancer tout le monde"
 *   logout            — "déconnecter"
 */

// ── Amount extraction ───────────────────────────────────────────────────────
const AMOUNT_RE = /(\d+(?:[.,]\d{1,2})?)\s*(?:euros?|eur|€)/i;
const AMOUNT_RE_SIMPLE = /(?:pay[eé]|vers[eé]|re[çc]u|donn[eé]|cheque|chèque|especes|espèces|virement)\s+(\d+(?:[.,]\d{1,2})?)/i;

function extractAmount(text) {
  const m = text.match(AMOUNT_RE) || text.match(AMOUNT_RE_SIMPLE);
  if (!m) return null;
  return parseFloat(m[1].replace(',', '.'));
}

// ── Year extraction ─────────────────────────────────────────────────────────
const YEAR_RE = /\b(20[12]\d)\b/;
function extractYear(text) {
  const m = text.match(YEAR_RE);
  return m ? parseInt(m[1], 10) : null;
}

// ── Day of week extraction ──────────────────────────────────────────────────
const DAY_NAMES = {
  'lundi': 1, 'mardi': 2, 'mercredi': 3, 'jeudi': 4,
  'vendredi': 5, 'samedi': 6, 'dimanche': 0,
};
const DAY_LABELS_FR = ['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi'];

function extractDayOfWeek(text) {
  const lower = text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  // Relative day keywords map to today's or tomorrow's day-of-week
  if (lower.includes('demain')) {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dow = tomorrow.getDay();
    return { dow, label: DAY_LABELS_FR[dow] };
  }
  if (lower.includes("aujourd'hui") || lower.includes('auj')) {
    const dow = new Date().getDay();
    return { dow, label: DAY_LABELS_FR[dow] };
  }
  for (const [name, dow] of Object.entries(DAY_NAMES)) {
    if (lower.includes(name)) return { dow, label: name };
  }
  return null;
}

/** Compute the next date (from today) for a given day-of-week (0=Sun). */
function nextDateForDow(dow) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayDow = today.getDay();
  const diff = ((dow - todayDow) + 7) % 7 || 7; // next occurrence, not today
  const next = new Date(today);
  next.setDate(next.getDate() + diff);
  return next;
}

function formatDateFr(d) {
  const day = d.getDate();
  const months = ['janvier','fevrier','mars','avril','mai','juin',
    'juillet','aout','septembre','octobre','novembre','decembre'];
  return `${DAY_LABELS_FR[d.getDay()]} ${day} ${months[d.getMonth()]}`;
}

function formatDateISO(d) {
  return d.toISOString().slice(0, 10);
}

// ── Time extraction ────────────────────────────────────────────────────────
const TIME_RE = /(\d{1,2})\s*[hH:]\s*(\d{0,2})/;
function extractTime(text) {
  const m = text.match(TIME_RE);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

// ── Month extraction ────────────────────────────────────────────────────────
const MONTH_NAMES = {
  'janvier': 1, 'fevrier': 2, 'février': 2, 'mars': 3, 'avril': 4,
  'mai': 5, 'juin': 6, 'juillet': 7, 'aout': 8, 'août': 8,
  'septembre': 9, 'octobre': 10, 'novembre': 11, 'decembre': 12, 'décembre': 12,
};
function extractMonth(text) {
  const lower = text.toLowerCase();
  for (const [name, num] of Object.entries(MONTH_NAMES)) {
    if (lower.includes(name)) return num;
  }
  return null;
}

// ── Entity matching (fuzzy) ─────────────────────────────────────────────────
function normalise(s) {
  return (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

// ── Signal matching (typo-tolerant) ─────────────────────────────────────────
const TYPO_MIN_LENGTH = 6;   // shorter command words must be exact ("cours" ≠ "jours")
const TYPO_MAX_EDITS = 1;    // "plannifier" → planifier, "paiment" → paiement

/** Levenshtein distance capped at `max` + 1 (early exit). */
function editDistance(a, b, max) {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i += 1) {
    const row = [i];
    let best = row[0];
    for (let j = 1; j <= b.length; j += 1) {
      row[j] = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      best = Math.min(best, row[j]);
    }
    if (best > max) return max + 1;
    prev = row;
  }
  return prev[b.length];
}

/**
 * True when the normalised text contains a signal. Single-word signals of ≥ TYPO_MIN_LENGTH letters also
 * match a query word one edit away. Multi-word signals stay exact.
 */
function hasSignal(lower, signals) {
  const words = lower.split(/[^a-z0-9']+/).filter(Boolean);
  return signals.some((signal) => {
    const s = normalise(signal);
    if (lower.includes(s)) return true;
    if (s.includes(' ') || s.length < TYPO_MIN_LENGTH) return false;
    return words.some((word) => word.length >= TYPO_MIN_LENGTH - 1
      && editDistance(word, s, TYPO_MAX_EDITS) <= TYPO_MAX_EDITS);
  });
}

function findMatchingStudents(tokens, students) {
  if (!tokens.length || !students.length) return [];
  const results = [];
  for (const st of students) {
    const first = normalise(st.first_name);
    const last = normalise(st.last_name);
    let score = 0;
    for (const t of tokens) {
      const tn = normalise(t);
      if (tn.length < 2) continue;
      if (first === tn || last === tn) score += 10;
      else if (first.startsWith(tn) || last.startsWith(tn)) score += 6;
      else if (first.includes(tn) || last.includes(tn)) score += 3;
    }
    if (score > 0) results.push({ ...st, _score: score });
  }
  results.sort((a, b) => b._score - a._score);
  return results.slice(0, 3);
}

function findMatchingFamilies(tokens, families) {
  if (!tokens.length || !families.length) return [];
  const results = [];
  for (const f of families) {
    const first = normalise(f.first_name);
    const last = normalise(f.last_name);
    const city = normalise(f.address_city || f.city_label || '');
    const phone = normalise(f.phone || f.phone_number || '');
    const email = normalise(f.email || '');
    let score = 0;
    for (const t of tokens) {
      const tn = normalise(t);
      if (tn.length < 2) continue;
      // Name matching (highest weight)
      if (first === tn || last === tn) score += 10;
      else if (first.startsWith(tn) || last.startsWith(tn)) score += 6;
      else if (first.includes(tn) || last.includes(tn)) score += 3;
      // City matching
      else if (city && (city === tn || city.startsWith(tn))) score += 5;
      else if (city && city.includes(tn) && tn.length >= 3) score += 2;
      // Phone matching (partial digits)
      else if (phone && tn.length >= 4 && phone.includes(tn)) score += 7;
      // Email matching
      else if (email && tn.length >= 3 && email.includes(tn)) score += 5;
    }
    if (score > 0) results.push({ ...f, _score: score });
  }
  results.sort((a, b) => b._score - a._score);
  return results.slice(0, 3);
}

function findFamilyForStudent(student, families) {
  if (!student?.family_id) return null;
  return families.find(f => f.family_id === student.family_id) || null;
}

// ── Intent patterns ─────────────────────────────────────────────────────────
// Each pattern: { test, intent, icon, buildProposals }
// test receives normalised lowercase text and returns truthy if it matches
// buildProposals receives context and returns proposal(s)

const STOP_WORDS = new Set([
  'le', 'la', 'les', 'un', 'une', 'des', 'de', 'du', 'au', 'aux',
  'et', 'ou', 'a', 'en', 'pour', 'par', 'sur', 'avec', 'dans',
  'mon', 'ma', 'mes', 'son', 'sa', 'ses', 'ce', 'cette', 'ces',
  'qui', 'que', 'est', 'sont', 'fait', 'ete', 'été',
  'me', 'te', 'se', 'nous', 'vous', 'ils', 'elles', 'il', 'elle',
  'm', 'l', 'd', 'n', 's', 'j', 'y', 'c',
]);

function entityTokens(text) {
  return normalise(text)
    .split(/\s+/)
    .filter(t => t.length >= 2 && !STOP_WORDS.has(t));
}

// ── Main hook ───────────────────────────────────────────────────────────────
export function useAssistantParser(query, families = [], students = [], stats = {}, toolOnly = false) {
  return useMemo(() => {
    const q = (query || '').trim();
    if (q.length < 2) return [];

    const lower = normalise(q);
    const toolProposals = searchCatalogueTools(q).map(({ tool, confidence }) => ({
      id: `tool-${tool.id}`,
      intent: 'open_tool',
      label: tool.label,
      desc: tool.hint,
      icon: 'Wrench',
      confidence,
      payload: { toolId: tool.id, href: tool.href },
    }));
    if (toolOnly) return toolProposals;
    const tokens = entityTokens(q);
    const amount = extractAmount(q);
    const year = extractYear(q);
    const month = extractMonth(q);

    const proposals = [];
    let id = 0;

    // ── Payment intent ────────────────────────────────────────────────────
    const paymentSignals = ['paye', 'payé', 'payer', 'verse', 'versé', 'verser',
      'recu', 'reçu', 'cheque', 'chèque', 'especes', 'espèces', 'virement',
      'paiement', 'enregistrer un paiement', 'reglement', 'règlement'];
    if (hasSignal(lower, paymentSignals)) {
      const matchedStudents = findMatchingStudents(tokens, students);
      const matchedFamilies = findMatchingFamilies(tokens, families);

      // If we matched a student, find their family
      if (matchedStudents.length > 0) {
        for (const st of matchedStudents.slice(0, 2)) {
          const fam = findFamilyForStudent(st, families);
          if (fam) {
            proposals.push({
              id: `pay-${++id}`,
              intent: 'record_payment',
              label: `Enregistrer un paiement pour la famille ${fam.first_name} ${fam.last_name}`,
              desc: amount ? `Montant : ${amount} EUR — via ${st.first_name}` : `Via ${st.first_name} ${st.last_name}`,
              icon: 'CreditCard',
              confidence: amount ? 0.95 : 0.8,
              payload: { family: fam, amount, student: st },
            });
          }
        }
      } else if (matchedFamilies.length > 0) {
        for (const fam of matchedFamilies.slice(0, 2)) {
          proposals.push({
            id: `pay-${++id}`,
            intent: 'record_payment',
            label: `Enregistrer un paiement pour la famille ${fam.first_name} ${fam.last_name}`,
            desc: amount ? `Montant : ${amount} EUR` : 'Ouvrir le formulaire de paiement',
            icon: 'CreditCard',
            confidence: amount ? 0.95 : 0.8,
            payload: { family: fam, amount },
          });
        }
      } else if (amount) {
        proposals.push({
          id: `pay-${++id}`,
          intent: 'record_payment',
          label: `Enregistrer un paiement de ${amount} EUR`,
          desc: 'Selectionnez la famille dans le formulaire',
          icon: 'CreditCard',
          confidence: 0.6,
          payload: { amount },
        });
      }
    }

    // ── Invoice intent ────────────────────────────────────────────────────
    const invoiceSignals = ['facture', 'facturer', 'generer', 'générer', 'invoice'];
    if (hasSignal(lower, invoiceSignals)) {
      const matchedStudents = findMatchingStudents(tokens, students);
      const matchedFamilies = findMatchingFamilies(tokens, families);
      const sendSignals = ['envoyer', 'envoi', 'email', 'mail'];
      const isSend = hasSignal(lower, sendSignals);
      const action = isSend ? 'Envoyer' : 'Generer';

      // Detect preset modes from natural language
      const presetMap = [
        { keys: ['impaye', 'impayé', 'impayés', 'impayes', 'cours impaye', 'cours impayés'], preset: 'unpaid', label: 'cours impayés', desc: 'Tous les cours non réglés' },
        { keys: ['dernier mois', 'mois dernier', 'mois précédent', 'mois precedent'], preset: 'last-1', label: 'dernier mois', desc: 'Cours du mois précédent' },
        { keys: ['3 derniers mois', 'trois derniers mois', 'trimestre'], preset: 'last-3', label: '3 derniers mois', desc: 'Cours des 3 derniers mois' },
        { keys: ['mois en cours', 'ce mois', 'mois actuel'], preset: 'current-month', label: 'mois en cours', desc: 'Cours du mois en cours' },
        { keys: ['dernier cours', 'derniere lecon', 'dernière leçon'], preset: 'last-lesson', label: 'dernier cours', desc: 'Uniquement le dernier cours' },
        { keys: ['annee derniere', 'année dernière', 'annee precedente'], preset: 'last-year', label: 'année dernière', desc: 'Tous les cours de l\'année précédente' },
        { keys: ['annee en cours', 'année en cours', 'cette annee', 'cette année'], preset: 'current-year', label: 'année en cours', desc: 'Tous les cours de l\'année' },
      ];
      const detectedPreset = presetMap.find(p => p.keys.some(k => lower.includes(normalise(k))));

      if (matchedStudents.length > 0) {
        for (const st of matchedStudents.slice(0, 2)) {
          const fam = findFamilyForStudent(st, families);
          if (fam) {
            const presetLabel = detectedPreset ? ` — ${detectedPreset.label}` : '';
            const yearLabel = !detectedPreset && year ? ` ${year}` : '';
            proposals.push({
              id: `inv-${++id}`,
              intent: 'generate_invoice',
              label: `${action} la facture${yearLabel} pour ${fam.first_name} ${fam.last_name}${presetLabel}`,
              desc: detectedPreset ? detectedPreset.desc : (`Eleve : ${st.first_name} ${st.last_name}${year ? ` — Annee ${year}` : ''}`),
              icon: 'Receipt',
              confidence: detectedPreset ? 0.95 : 0.9,
              payload: { family: fam, student: st, year, send: isSend, preset: detectedPreset?.preset },
            });
          }
        }
      } else if (matchedFamilies.length > 0) {
        for (const fam of matchedFamilies.slice(0, 2)) {
          const presetLabel = detectedPreset ? ` — ${detectedPreset.label}` : '';
          const yearLabel = !detectedPreset && year ? ` ${year}` : '';
          proposals.push({
            id: `inv-${++id}`,
            intent: 'generate_invoice',
            label: `${action} la facture${yearLabel} pour ${fam.first_name} ${fam.last_name}${presetLabel}`,
            desc: detectedPreset ? detectedPreset.desc : (year ? `Annee ${year}` : 'Ouvrir le generateur de factures'),
            icon: 'Receipt',
            confidence: detectedPreset ? 0.95 : 0.85,
            payload: { family: fam, year, send: isSend, preset: detectedPreset?.preset },
          });
        }
      } else {
        const presetLabel = detectedPreset ? ` — ${detectedPreset.label}` : '';
        proposals.push({
          id: `inv-${++id}`,
          intent: 'generate_invoice',
          label: `${action} une facture${year ? ` ${year}` : ''}${presetLabel}`,
          desc: detectedPreset ? detectedPreset.desc : 'Ouvrir le generateur de factures',
          icon: 'Receipt',
          confidence: detectedPreset ? 0.9 : 0.6,
          payload: { year, send: isSend, preset: detectedPreset?.preset },
        });
      }
    }

    // ── Attestation fiscale SAP intent ────────────────────────────────────
    const attestationSignals = ['attestation', 'sap', '7db', 'case 7db', 'fiscale', 'impots sap', 'credit impot sap'];
    if (hasSignal(lower, attestationSignals)) {
      const matchedFamilies = findMatchingFamilies(tokens, families);
      const targetYear = year || (new Date().getFullYear() - 1);
      if (matchedFamilies.length > 0) {
        for (const fam of matchedFamilies.slice(0, 2)) {
          proposals.push({
            id: `att-${++id}`,
            intent: 'attestation_sap',
            label: `Attestation fiscale SAP ${targetYear} — famille ${fam.first_name} ${fam.last_name}`,
            desc: `Générer l'attestation SAP pour la déclaration de revenus (case 7DB)`,
            icon: 'Shield',
            confidence: 0.95,
            payload: { family: fam, year: targetYear },
          });
        }
      } else {
        proposals.push({
          id: `att-${++id}`,
          intent: 'attestation_sap',
          label: `Attestation fiscale SAP ${targetYear}`,
          desc: 'Générer l\'attestation SAP pour toutes les familles (case 7DB)',
          icon: 'Shield',
          confidence: 0.75,
          payload: { year: targetYear },
        });
      }
    }

    // ── Course intent ─────────────────────────────────────────────────────
    const courseSignals = ['cours', 'lecon', 'leçon', 'commencer', 'demarrer', 'démarrer'];
    if (hasSignal(lower, courseSignals)) {
      const matchedStudents = findMatchingStudents(tokens, students);
      if (matchedStudents.length > 0) {
        for (const st of matchedStudents.slice(0, 3)) {
          proposals.push({
            id: `course-${++id}`,
            intent: 'start_course',
            label: `Demarrer un cours avec ${st.first_name} ${st.last_name}`,
            desc: st.class_level || '',
            icon: 'Play',
            confidence: 0.9,
            payload: { student: st },
          });
        }
      } else {
        proposals.push({
          id: `course-${++id}`,
          intent: 'start_course',
          label: 'Demarrer un nouveau cours',
          desc: 'Choisir un eleve',
          icon: 'Play',
          confidence: 0.5,
          payload: {},
        });
      }
    }

    // ── View student / family intent ──────────────────────────────────────
    // Note: 'profil' excluded — it conflicts with settings/profile intent
    const viewSignals = ['fiche', 'voir', 'detail', 'détail', 'consulter', 'famille', 'parent'];
    const hasViewSignal = hasSignal(lower, viewSignals);
    if (hasViewSignal || tokens.length <= 3) {
      const matchedStudents = findMatchingStudents(tokens, students);
      const matchedFamilies = findMatchingFamilies(tokens, families);

      // Always inject matched student fiches when name found
      for (const st of matchedStudents.slice(0, 2)) {
        const already = proposals.some(p => p.payload?.student?.student_id === st.student_id);
        if (!already) {
          proposals.push({
            id: `view-st-${++id}`,
            intent: 'view_student',
            label: `Fiche de ${st.first_name} ${st.last_name}`,
            desc: st.class_level || '',
            icon: 'User',
            confidence: hasViewSignal ? 0.85 : 0.4,
            payload: { student: st },
          });
        }
        // Always inject the student's family alongside, avoiding duplication
        const fam = findFamilyForStudent(st, families);
        if (fam) {
          const famAlready = proposals.some(p => p.payload?.family?.family_id === fam.family_id);
          if (!famAlready) {
            proposals.push({
              id: `view-fam-${++id}`,
              intent: 'view_family',
              label: `Famille ${fam.first_name} ${fam.last_name}`,
              desc: [fam.city_label || fam.address_city].filter(Boolean).join(' · ') || '',
              icon: 'Users',
              confidence: 0.35,
              payload: { family: fam },
            });
          }
        }
      }

      // Also inject directly-matched families
      for (const fam of matchedFamilies.slice(0, 2)) {
        const already = proposals.some(p => p.payload?.family?.family_id === fam.family_id);
        if (!already) {
          proposals.push({
            id: `view-fam-${++id}`,
            intent: 'view_family',
            label: `Famille ${fam.first_name} ${fam.last_name}`,
            desc: [fam.city_label || fam.address_city].filter(Boolean).join(' · ') || '',
            icon: 'Users',
            confidence: hasViewSignal ? 0.85 : 0.35,
            payload: { family: fam },
          });
        }
      }
    }

    // ── Schedule intent ───────────────────────────────────────────────────
    const schedSignals = ['planifier', 'programmer', 'calendrier', 'schedule', 'planning', 'emploi du temps',
      'agenda', 'horaire', 'creneau', 'créneau'];
    if (hasSignal(lower, schedSignals)) {
      const matchedStudents = findMatchingStudents(tokens, students);
      const parsedDay = extractDayOfWeek(q);
      const parsedTime = extractTime(q);

      if (matchedStudents.length > 0) {
        const st = matchedStudents[0];

        if (parsedDay) {
          // Smart planning: student + day (+ optional time)
          const nextD = nextDateForDow(parsedDay.dow);
          const dateFr = formatDateFr(nextD);
          const timeLabel = parsedTime ? ` a ${parsedTime.replace(':', 'h')}` : '';

          // Proposal 1: one-time on specific date
          proposals.push({
            id: `sched-${++id}`,
            intent: 'schedule',
            label: `Cours avec ${st.first_name} ${dateFr}${timeLabel}`,
            desc: `Cours ponctuel le ${dateFr}`,
            icon: 'Calendar',
            confidence: 0.95,
            payload: {
              student: st,
              day_of_week: parsedDay.dow,
              start_time: parsedTime,
              start_date: formatDateISO(nextD),
              recurrence: 'once',
            },
          });

          // Proposal 2: weekly recurring
          proposals.push({
            id: `sched-${++id}`,
            intent: 'schedule',
            label: `Cours avec ${st.first_name} tous les ${parsedDay.label}s${timeLabel}`,
            desc: `Cours hebdomadaire a partir du ${dateFr}`,
            icon: 'Calendar',
            confidence: 0.9,
            payload: {
              student: st,
              day_of_week: parsedDay.dow,
              start_time: parsedTime,
              start_date: formatDateISO(nextD),
              recurrence: 'weekly',
            },
          });
        } else {
          // Student found but no day specified — open their schedule form
          for (const s of matchedStudents.slice(0, 2)) {
            proposals.push({
              id: `sched-${++id}`,
              intent: 'schedule',
              label: `Modifier le planning de ${s.first_name} ${s.last_name}`,
              desc: s.class_level || 'Ouvrir le formulaire de planning',
              icon: 'Calendar',
              confidence: 0.85,
              payload: { student: s, openForm: true },
            });
          }
        }
      } else {
        // No student match
        if (parsedDay) {
          // Day parsed but unknown student → propose new student + schedule manager
          const timeLabel = parsedTime ? ` a ${parsedTime.replace(':', 'h')}` : '';
          proposals.push({
            id: `sched-${++id}`,
            intent: 'schedule',
            label: `Planifier un cours avec un nouvel eleve${timeLabel}`,
            desc: `${parsedDay.label}${timeLabel} — choisir l'eleve`,
            icon: 'Calendar',
            confidence: 0.88,
            payload: {
              student: null,
              day_of_week: parsedDay.dow,
              start_time: parsedTime,
              start_date: formatDateISO(nextDateForDow(parsedDay.dow)),
              recurrence: 'once',
              new_student: true,
              openForm: true,
            },
          });
        }
        proposals.push({
          id: `sched-${++id}`,
          intent: 'schedule',
          label: 'Ouvrir le gestionnaire de plannings',
          desc: 'Creer ou modifier un creneau',
          icon: 'Calendar',
          confidence: 0.65,
          payload: { openForm: true },
        });
      }
    }

    // ── Registration intent ───────────────────────────────────────────────
    const registerSignals = ['inscrire', 'inscription', 'ajouter', 'nouveau', 'nouvel', 'nouvelle'];
    if (hasSignal(lower, registerSignals)) {
      if (lower.includes('famille')) {
        proposals.push({
          id: `reg-${++id}`,
          intent: 'add_family',
          label: 'Inscrire une nouvelle famille',
          desc: 'Creer une fiche famille',
          icon: 'Users',
          confidence: 0.85,
          payload: {},
        });
      }
      if (lower.includes('eleve') || lower.includes('élève') || lower.includes('etudiant')) {
        proposals.push({
          id: `reg-${++id}`,
          intent: 'add_student',
          label: 'Inscrire un nouvel eleve',
          desc: 'Creer une fiche eleve',
          icon: 'GraduationCap',
          confidence: 0.85,
          payload: {},
        });
      }
      // Generic register if no specific target
      if (!lower.includes('famille') && !lower.includes('eleve') && !lower.includes('élève')) {
        proposals.push(
          { id: `reg-${++id}`, intent: 'add_family', label: 'Inscrire une famille', desc: '', icon: 'Users', confidence: 0.6, payload: {} },
          { id: `reg-${++id}`, intent: 'add_student', label: 'Inscrire un eleve', desc: '', icon: 'GraduationCap', confidence: 0.6, payload: {} },
        );
      }
    }

    // ── Stats intent (granular — maps to specific tabs + sections) ─────
    const fmtEur = (v) => `${Number(v || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} EUR`;

    // Revenue-related
    const revenueWords = ['revenu', 'ca', 'chiffre d\'affaire', 'argent', 'gains',
      'combien j\'ai gagne', 'combien je gagne', 'recette', 'benefice', 'bénéfice'];
    const monthWords = ['mois', 'mensuel', 'dernier mois', 'ce mois', 'mois dernier'];
    const yearWords = ['annuel', 'annee', 'année'];
    const weekWords = ['semaine', 'hebdo', 'hebdomadaire', 'cette semaine'];
    const hasRevenue = hasSignal(lower, revenueWords);
    const hasMonth = hasSignal(lower, monthWords);
    const hasYear = hasSignal(lower, yearWords);
    const hasWeek = hasSignal(lower, weekWords);

    // "combien d'eleves", "combien de cours", "combien d'heures"
    const countWords = ['combien'];
    const hasCount = hasSignal(lower, countWords);
    if (hasCount && !hasRevenue) {
      if (lower.includes('eleve') || lower.includes('élève') || lower.includes('etudiant')) {
        proposals.push({
          id: `stats-${++id}`, intent: 'stats',
          label: `${stats.active_students || 0} eleves actifs`,
          desc: `${stats.total_students || 0} au total`,
          icon: 'User', confidence: 0.95,
          payload: { tab: 'students' },
        });
      }
      if (lower.includes('cours') || lower.includes('lecon')) {
        proposals.push({
          id: `stats-${++id}`, intent: 'stats',
          label: `${stats.total_courses || 0} cours donnes`,
          desc: `${stats.total_hours || 0}h au total`,
          icon: 'ClipboardList', confidence: 0.95,
          payload: { tab: 'revenues' },
        });
      }
      if (lower.includes('famille')) {
        proposals.push({
          id: `stats-${++id}`, intent: 'stats',
          label: `${stats.families || 0} familles actives`,
          desc: `${stats.total_families || 0} au total`,
          icon: 'Users', confidence: 0.95,
          payload: { tab: 'students' },
        });
      }
      if (lower.includes('heure')) {
        proposals.push({
          id: `stats-${++id}`, intent: 'stats',
          label: `${stats.total_hours || 0} heures de cours`,
          desc: `${stats.total_courses || 0} cours au total`,
          icon: 'Clock', confidence: 0.95,
          payload: { tab: 'revenues' },
        });
      }
    }

    if (hasRevenue || hasMonth || hasYear || hasWeek) {
      const revenueInfo = stats.total_revenue ? ` — Total: ${fmtEur(stats.total_revenue)}` : '';

      if (hasMonth) {
        proposals.push({
          id: `stats-${++id}`, intent: 'stats',
          label: 'CA mensuel', desc: `Graphique du chiffre d'affaires par mois${revenueInfo}`,
          icon: 'TrendingUp', confidence: 0.9,
          payload: { tab: 'revenues', scrollTo: 'chart-monthly-civil' },
        });
      }
      if (hasYear) {
        proposals.push({
          id: `stats-${++id}`, intent: 'stats',
          label: 'CA annuel', desc: `Revenus par annee${revenueInfo}`,
          icon: 'BarChart2', confidence: 0.9,
          payload: { tab: 'revenues', scrollTo: 'chart-yearly' },
        });
      }
      if (hasWeek) {
        proposals.push({
          id: `stats-${++id}`, intent: 'stats',
          label: 'CA hebdomadaire', desc: 'Revenus de la semaine',
          icon: 'BarChart2', confidence: 0.9,
          payload: { tab: 'revenues', scrollTo: 'chart-weekly' },
        });
      }
      if (!hasMonth && !hasYear && !hasWeek) {
        proposals.push({
          id: `stats-${++id}`, intent: 'stats',
          label: stats.total_revenue ? `CA total: ${fmtEur(stats.total_revenue)}` : 'Mes revenus',
          desc: stats.total_revenue ? `${stats.total_courses || 0} cours, ${stats.total_hours || 0}h` : 'Tous les graphiques de revenus',
          icon: 'TrendingUp', confidence: 0.8,
          payload: { tab: 'revenues' },
        });
      }
    }

    // Payment method / taux horaire
    if (hasSignal(lower, ['taux horaire', 'tarif', 'prix', 'prix moyen'])) {
      proposals.push({
        id: `stats-${++id}`, intent: 'stats',
        label: 'Taux horaire moyen', desc: 'Evolution du tarif horaire',
        icon: 'TrendingUp', confidence: 0.9,
        payload: { tab: 'revenues', scrollTo: 'chart-avg-hourly' },
      });
    }
    if (hasSignal(lower, ['moyen de paiement', 'moyens paiement', 'comment payent', 'comment paient'])) {
      proposals.push({
        id: `stats-${++id}`, intent: 'stats',
        label: 'Moyens de paiement', desc: 'Repartition cheque, virement, especes',
        icon: 'CreditCard', confidence: 0.9,
        payload: { tab: 'revenues', scrollTo: 'chart-payment-methods' },
      });
    }
    if (hasSignal(lower, ['top famille', 'meilleur client', 'plus gros', 'meilleurs familles'])) {
      proposals.push({
        id: `stats-${++id}`, intent: 'stats',
        label: 'Top familles', desc: 'Familles generant le plus de revenus',
        icon: 'Users', confidence: 0.9,
        payload: { tab: 'revenues', scrollTo: 'chart-top-families' },
      });
    }

    // Student stats
    if (hasSignal(lower, ['note', 'evaluation', 'moyenne', 'progression', 'evolution note',
         'resultat', 'résultat']) && !proposals.some(p => p.intent === 'grades')) {
      proposals.push({
        id: `stats-${++id}`, intent: 'stats',
        label: 'Evolution des notes', desc: 'Graphiques notes et moyennes par eleve',
        icon: 'BarChart2', confidence: 0.85,
        payload: { tab: 'students', scrollTo: 'chart-student-evolution' },
      });
    }
    if (hasSignal(lower, ['retention', 'rétention', 'fidel', 'fidélisation'])) {
      proposals.push({
        id: `stats-${++id}`, intent: 'stats',
        label: 'Retention des eleves', desc: 'Taux de fidelisation',
        icon: 'BarChart2', confidence: 0.85,
        payload: { tab: 'students', scrollTo: 'chart-retention' },
      });
    }

    // Logs / suivi
    if (hasSignal(lower, ['historique', 'suivi', 'log', 'journal'])) {
      proposals.push({
        id: `stats-${++id}`, intent: 'stats',
        label: 'Suivi detaille', desc: 'Historique paiements et cours',
        icon: 'ClipboardList', confidence: 0.8,
        payload: { tab: 'logs' },
      });
    }

    // Generic fallback stats
    if (hasSignal(lower, ['statistique', 'stats', 'bilan', 'dashboard', 'tableau de bord'])
        && !proposals.some(p => p.intent === 'stats')) {
      proposals.push({
        id: `stats-${++id}`, intent: 'stats',
        label: 'Statistiques', desc: 'Revenus, eleves, suivi',
        icon: 'BarChart2', confidence: 0.7,
        payload: { tab: 'revenues' },
      });
    }

    // ── Relance (dunning) intent ──────────────────────────────────────────
    // "relancer tous les impayés", "relancer tout le monde",
    // "relance globale", "envoyer relance"
    const relanceSignals = ['relance', 'relancer', 'rappel impaye', 'rappel impayé'];
    const matchesRelance = hasSignal(lower, relanceSignals);
    if (matchesRelance) {
      const matchedFamilies = findMatchingFamilies(tokens, families);
      const globalSignals = [
        'tous', 'toutes', 'tout le monde', 'tout monde',
        'impaye', 'impayé', 'impayes', 'impayés',
        'globale', 'global', 'en masse', 'bulk',
      ];
      const wantsGlobal = hasSignal(lower, globalSignals);

      if (matchedFamilies.length > 0 && !wantsGlobal) {
        // Targeted relance on a specific family
        for (const fam of matchedFamilies.slice(0, 2)) {
          proposals.push({
            id: `relance-${++id}`,
            intent: 'relance_family',
            label: `Relancer la famille ${fam.first_name} ${fam.last_name}`,
            desc: 'Ouvrir la composition d\'email de relance',
            icon: 'Send',
            confidence: 0.9,
            payload: { family: fam },
          });
        }
      } else {
        // Bulk relance — open the pending-payments list with composer preloaded
        proposals.push({
          id: `relance-${++id}`,
          intent: 'relance_all',
          label: 'Relancer tous les impayés',
          desc: 'Email groupé à toutes les familles avec solde dû',
          icon: 'Send',
          confidence: 0.92,
          payload: {},
        });
      }
    }

    // ── URSSAF intent ─────────────────────────────────────────────────────
    const urssafSignals = ['urssaf', 'avance immediate', 'avance immédiate', 'nova', 'cesu',
      'declaration', 'déclaration'];
    if (hasSignal(lower, urssafSignals)) {
      if (lower.includes('identifiant') || lower.includes('configurer') || lower.includes('credential')) {
        proposals.push({
          id: `urssaf-${++id}`,
          intent: 'urssaf_credentials',
          label: 'Configurer identifiants URSSAF',
          desc: 'Ouvrir la section accreditations',
          icon: 'Shield',
          confidence: 0.9,
          payload: {},
        });
      } else {
        proposals.push({
          id: `urssaf-${++id}`,
          intent: 'urssaf',
          label: 'Avance immediate URSSAF',
          desc: 'Consulter les demandes',
          icon: 'Shield',
          confidence: 0.8,
          payload: {},
        });
      }
    }

    // ── Settings intent ───────────────────────────────────────────────────
    if (hasSignal(lower, ['parametre', 'paramètre', 'setting', 'config', 'profil',
         'mon profil', 'mon compte', 'compte', 'preference', 'préférence',
         'information', 'mes infos', 'modifier profil'])) {
      proposals.push({
        id: `settings-${++id}`,
        intent: 'settings',
        label: 'Mon profil',
        desc: 'Modifier vos informations personnelles',
        icon: 'Settings',
        confidence: 0.8,
        payload: {},
      });
    }

    // ── Logout intent ─────────────────────────────────────────────────────
    if (hasSignal(lower, ['deconnecter', 'déconnecter', 'logout', 'quitter', 'sortir'])) {
      proposals.push({
        id: `logout-${++id}`,
        intent: 'logout',
        label: 'Se deconnecter',
        desc: '',
        icon: 'LogOut',
        confidence: 0.9,
        payload: {},
      });
    }

    // ── Deduplicate by intent+entity and sort by confidence ─────────────
    proposals.push(...toolProposals);
    const seen = new Set();
    const unique = proposals.filter(p => {
      const key = `${p.intent}-${p.payload?.toolId || ''}-${p.payload?.family?.family_id || ''}-${p.payload?.student?.student_id || ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    unique.sort((a, b) => b.confidence - a.confidence);
    return unique.slice(0, 6);
  }, [query, families, students, stats, toolOnly]);
}
